/**
 * Utility functions for semantic version comparison
 */

/**
 * Parse a semantic version string into its components
 * @param version - Semantic version string (e.g., "1.2.3")
 * @returns Object with major, minor, and patch numbers
 */
function parseVersion(version: string): { major: number; minor: number; patch: number } {
  const parts = version.split('.').map(part => parseInt(part, 10));
  return {
    major: parts[0] || 0,
    minor: parts[1] || 0,
    patch: parts[2] || 0,
  };
}

/**
 * Compare two semantic version strings
 * @param version1 - First version string
 * @param version2 - Second version string
 * @returns -1 if version1 < version2, 0 if equal, 1 if version1 > version2
 */
export function compareVersions(version1: string, version2: string): number {
  const v1 = parseVersion(version1);
  const v2 = parseVersion(version2);

  if (v1.major !== v2.major) {
    return v1.major < v2.major ? -1 : 1;
  }

  if (v1.minor !== v2.minor) {
    return v1.minor < v2.minor ? -1 : 1;
  }

  if (v1.patch !== v2.patch) {
    return v1.patch < v2.patch ? -1 : 1;
  }

  return 0;
}

/**
 * Check if an agent version is outdated compared to the current version
 * @param agentVersion - Agent's version string (can be null)
 * @param currentVersion - Current/expected version string
 * @returns true if agent version is outdated, false otherwise
 */
export function isAgentOutdated(agentVersion: string | null, currentVersion: string): boolean {
  if (!agentVersion) {
    // Don't show outdated indicator if agent version is unknown
    return false;
  }

  try {
    return compareVersions(agentVersion, currentVersion) < 0;
  } catch (error) {
    // If version parsing fails, assume not outdated
    console.warn('Failed to parse version:', { agentVersion, currentVersion }, error);
    return false;
  }
}

/**
 * Get a human-readable version display string
 * @param version - Version string or null
 * @returns Formatted version string or null if no version
 */
export function formatVersionDisplay(version: string | null): string | null {
  if (!version) {
    return null;
  }
  
  return `v${version}`;
}